import React, { useState, useEffect } from 'react';
import { Calculator, Users, Percent, AlertTriangle } from 'lucide-react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const ABTestSignificanceCalculator = () => {
  const [visitorsA, setVisitorsA] = useState(5000);
  const [conversionsA, setConversionsA] = useState(100);
  const [visitorsB, setVisitorsB] = useState(5000);
  const [conversionsB, setConversionsB] = useState(120);
  const [confidenceLevel, setConfidenceLevel] = useState(95);

  const [conversionRateA, setConversionRateA] = useState(0);
  const [conversionRateB, setConversionRateB] = useState(0);
  const [improvement, setImprovement] = useState(0);
  const [zScore, setZScore] = useState(0);
  const [pValue, setPValue] = useState(0);
  const [isSignificant, setIsSignificant] = useState(false);

  useEffect(() => {
    calculateResults();
  }, [visitorsA, conversionsA, visitorsB, conversionsB, confidenceLevel]);

  const calculateResults = () => {
    const rateA = conversionsA / visitorsA;
    const rateB = conversionsB / visitorsB;
    setConversionRateA(rateA);
    setConversionRateB(rateB);

    const improvementCalc = ((rateB - rateA) / rateA) * 100;
    setImprovement(parseFloat(improvementCalc.toFixed(2)));

    const pooledStandardError = Math.sqrt(
      rateA * (1 - rateA) / visitorsA + rateB * (1 - rateB) / visitorsB
    );
    const zScoreCalc = (rateB - rateA) / pooledStandardError;
    setZScore(parseFloat(zScoreCalc.toFixed(2)));

    // Approximate p-value calculation
    const pValueCalc = 2 * (1 - normalcdf(Math.abs(zScoreCalc)));
    setPValue(parseFloat(pValueCalc.toFixed(4)));

    setIsSignificant(pValueCalc < (1 - confidenceLevel / 100));
  };

  // Helper function to calculate the cumulative distribution function of the standard normal distribution
  const normalcdf = (x) => {
    const t = 1 / (1 + 0.2316419 * Math.abs(x));
    const d = 0.3989423 * Math.exp(-x * x / 2);
    let prob = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    if (x > 0) prob = 1 - prob;
    return prob;
  };

  const chartData = {
    labels: ['Variant A', 'Variant B'],
    datasets: [
      {
        data: [conversionRateA, conversionRateB],
        backgroundColor: ['rgba(255, 99, 132, 0.6)', 'rgba(54, 162, 235, 0.6)'],
      },
    ],
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        A/B Test Significance Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Visitors A</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={visitorsA}
                  onChange={(e) => setVisitorsA(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Conversions A</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Percent className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={conversionsA}
                  onChange={(e) => setConversionsA(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Visitors B</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={visitorsB}
                  onChange={(e) => setVisitorsB(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Conversions B</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Percent className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={conversionsB}
                  onChange={(e) => setConversionsB(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="120"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Confidence Level (%)</label>
              <input
                type="number"
                value={confidenceLevel}
                onChange={(e) => setConfidenceLevel(Number(e.target.value))}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
                placeholder="95"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Conversion Rate A</p>
              <p className="text-2xl font-bold text-indigo-600">{(conversionRateA * 100).toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Conversion Rate B</p>
              <p className="text-2xl font-bold text-indigo-600">{(conversionRateB * 100).toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Improvement</p>
              <p className="text-2xl font-bold text-green-600">{improvement}%</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Z-Score</p>
              <p className="text-2xl font-bold text-blue-600">{zScore}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">P-Value</p>
              <p className="text-2xl font-bold text-purple-600">{pValue}</p>
            </div>
            <div className="flex items-center">
              <AlertTriangle className={`w-6 h-6 mr-2 ${isSignificant ? 'text-green-500' : 'text-red-500'}`} />
              <p className={`text-lg font-semibold ${isSignificant ? 'text-green-600' : 'text-red-600'}`}>
                {isSignificant ? 'Statistically Significant' : 'Not Statistically Significant'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Conversion Rate Comparison</h2>
        <div className="w-full max-w-md mx-auto">
          <Pie data={chartData} />
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation</h2>
        <p className="text-gray-600">
          {isSignificant
            ? `The A/B test results are statistically significant at the ${confidenceLevel}% confidence level. This means that the observed difference between Variant A and Variant B is unlikely to have occurred by chance.`
            : `The A/B test results are not statistically significant at the ${confidenceLevel}% confidence level. This means that the observed difference between Variant A and Variant B could have occurred by chance.`}
        </p>
        <p className="text-gray-600 mt-2">
          The improvement of {improvement}% in the conversion rate suggests that {improvement > 0 ? 'Variant B outperforms Variant A' : 'Variant A outperforms Variant B'}.
          However, always consider practical significance alongside statistical significance when making decisions.
        </p>
        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Next Steps</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>If significant, consider implementing the winning variant</li>
          <li>If not significant, you may need to run the test for longer or with a larger sample size</li>
          <li>Always consider other factors such as user experience and long-term effects</li>
          <li>Continue to test and iterate on your designs and strategies</li>
        </ul>
      </div>
    </div>
  );
};

export default ABTestSignificanceCalculator;